import React, { useState, useEffect, useMemo } from 'react';
import { useI18n } from './i18n';
import { getCurrentUserId } from './attachments';
import { matchKey } from './codes';
import { sheetOwnership } from './WineSheetPicker';
import { isFilled } from './foodSheetImport';

// Groups the food sheets visible to the user by dish. The dish typed in the form (if any) comes first, then the dishes
// with sheets of the user, then the others by name. Within a dish the user's sheets come first, then the most recent.
export function groupFoodSheetsByDish(sheets, me, currentDish) {
  const currentKey = matchKey(currentDish);
  const dishes = new Map();
  (sheets || []).forEach((sheet) => {
    const key = matchKey(sheet.dish_name);
    if (!key) return;
    if (!dishes.has(key)) dishes.set(key, { key, name: sheet.dish_name, sheets: [], sameAsForm: !!currentKey && key === currentKey });
    dishes.get(key).sheets.push(sheet);
  });
  dishes.forEach((dish) => {
    dish.sheets.sort((a, b) => {
      const ownA = sheetOwnership(a, me) === 'own' ? 0 : 1;
      const ownB = sheetOwnership(b, me) === 'own' ? 0 : 1;
      if (ownA !== ownB) return ownA - ownB;
      return String(b.tasting_date || b.created_at || '').localeCompare(String(a.tasting_date || a.created_at || ''));
    });
    dish.counts = { own: 0, anonymous: 0, public: 0 };
    dish.sheets.forEach((sheet) => {
      const who = sheetOwnership(sheet, me);
      if (who === 'own') dish.counts.own += 1;
      else if (who === 'anonymous') dish.counts.anonymous += 1;
      else if (sheet.visibility === 'public') dish.counts.public += 1;
    });
  });
  return [...dishes.values()].sort((a, b) => {
    if (a.sameAsForm !== b.sameAsForm) return a.sameAsForm ? -1 : 1;
    if ((a.counts.own > 0) !== (b.counts.own > 0)) return a.counts.own > 0 ? -1 : 1;
    return a.key.localeCompare(b.key);
  });
}

const mean = (value) => (typeof value === 'number' ? Math.round(value * 10) / 10 : null);

// Searchable list of ALL the food sheets visible to the user, grouped by dish, with badges, and the choice of ONE sheet
// (radio): no averaging between sheets. "Import" replaces the 12 values (the pairing keeps the link to that food sheet; a value
// the sheet did not fill in stays not filled in) and "Complete empty values" fills only the parameters that are still empty
// (available only for a sheet of the dish in the form).
// Props: sheets (API food-sheet objects), dishName (dish typed in the form), onImport({ mode: 'replace' | 'complete', sheet }).
function FoodSheetPicker({ sheets, dishName, onImport }) {
  const { t, formatDate } = useI18n();
  const [me, setMe] = useState(null);
  const [query, setQuery] = useState('');
  const [dishKey, setDishKey] = useState(null);
  const [sheetId, setSheetId] = useState(null);        // the ONE chosen sheet

  useEffect(() => {
    let abort = false;
    getCurrentUserId().then((id) => { if (!abort) setMe(id || null); }).catch(() => {});
    return () => { abort = true; };
  }, []);

  const dishes = useMemo(() => groupFoodSheetsByDish(sheets, me, dishName), [sheets, me, dishName]);
  const filtered = useMemo(() => {
    const key = matchKey(query);
    if (!key) return dishes;
    return dishes.filter((dish) => matchKey(`${dish.name} ${dish.sheets.map((s) => `${s.chef || ''} ${s.venue || ''}`).join(' ')}`).includes(key));
  }, [dishes, query]);
  const selected = dishes.find((dish) => dish.key === dishKey) || null;

  const choose = (dish) => {
    if (selected && selected.key === dish.key) { setDishKey(null); return; }
    setDishKey(dish.key);
    setSheetId(dish.sheets.length ? dish.sheets[0].id : null);        // the first one (yours first, then the most recent)
  };

  const chosenSheet = selected ? selected.sheets.find((sheet) => sheet.id === sheetId) || null : null;
  // Completing only makes sense on the dish of the form
  const sameDish = (dish) => !!matchKey(dishName) && dish.key === matchKey(dishName);

  const doImport = (mode) => {
    if (!chosenSheet) return;
    onImport({ mode, sheet: chosenSheet });
  };

  if (dishes.length === 0) return <div className="form-field import-onav"><small className="import-hint">{t('pairing.foodpicker.none')}</small></div>;

  const who = (sheet) => {
    const ownership = sheetOwnership(sheet, me);
    if (ownership === 'own') return t('pairing.picker.you');
    if (ownership === 'anonymous') return t('pairing.picker.anonymous');
    return sheet.taster || t('pairing.picker.other');
  };
  const keyValues = (sheet) => [
    `${t('pairing.foodpicker.k_structure')} ${isFilled(sheet.food_structure) ? sheet.food_structure : '—'}`,
    `${t('pairing.foodpicker.k_aromaticity')} ${isFilled(sheet.food_aromaticity) ? sheet.food_aromaticity : '—'}`,
    `${t('pairing.foodpicker.k_persistence')} ${isFilled(sheet.food_persistence) ? sheet.food_persistence : '—'}`,
  ].join(' · ');
  const phaseMeans = (sheet) => {
    const parts = [
      ['visual', mean(sheet.visual_mean)], ['olfactory', mean(sheet.olfactory_mean)], ['final', mean(sheet.final_mean)],
    ].filter(([, value]) => value !== null);
    return parts.length ? parts.map(([name, value]) => `${t(`pairing.foodpicker.m_${name}`)} ${value}`).join(' · ') : null;
  };

  return (
    <div className="form-field import-onav food-picker">
      <label>{t('pairing.food.recall_label')}</label>
      <input type="search" className="food-picker-search" value={query} onChange={(e) => setQuery(e.target.value)}
        placeholder={t('pairing.foodpicker.search')} aria-label={t('pairing.foodpicker.search')} />
      <small className="import-hint">{t('pairing.food.hint')}</small>

      {filtered.length === 0 ? (
        <p className="empty">{t('pairing.foodpicker.no_match')}</p>
      ) : (
        <ul className="food-picker-list" role="listbox" aria-label={t('pairing.food.recall_label')}>
          {filtered.map((dish) => (
            <li key={dish.key} role="option" aria-selected={!!selected && selected.key === dish.key}
              className={`food-picker-dish${selected && selected.key === dish.key ? ' selected' : ''}${dish.sameAsForm ? ' same' : ''}`}>
              <button type="button" className="food-picker-row" onClick={() => choose(dish)}>
                <span className="food-picker-name">
                  <strong>{dish.name}</strong>
                  {dish.sameAsForm && <span className="food-picker-badge same">{t('pairing.foodpicker.same_dish')}</span>}
                </span>
                <span className="food-picker-badges">
                  <span className="food-picker-badge count">{t('pairing.foodpicker.sheets', { count: dish.sheets.length })}</span>
                  {dish.counts.own > 0 && <span className="food-picker-badge own">{t('pairing.picker.yours', { count: dish.counts.own })}</span>}
                  {dish.counts.public > 0 && <span className="food-picker-badge public">{t('pairing.picker.public', { count: dish.counts.public })}</span>}
                  {dish.counts.anonymous > 0 && <span className="food-picker-badge anon">{t('pairing.picker.anonymous_count', { count: dish.counts.anonymous })}</span>}
                </span>
              </button>

              {selected && selected.key === dish.key && (
                <div className="food-picker-sheets">
                  <div className="food-picker-sheets-title">{t('pairing.foodpicker.sheets_title')}</div>
                  {dish.sheets.map((sheet) => (
                    <label key={sheet.id} className={`food-picker-sheet${sheetId === sheet.id ? ' checked' : ''}`}>
                      <input type="radio" name="food-picker-sheet" checked={sheetId === sheet.id} onChange={() => setSheetId(sheet.id)} />
                      <span className="food-picker-sheet-info">
                        {formatDate(sheet.tasting_date || (sheet.created_at || '').slice(0, 10)) || '—'} · {who(sheet)}
                        {[sheet.chef, sheet.venue].filter(Boolean).length > 0 && <> · {[sheet.chef, sheet.venue].filter(Boolean).join(' · ')}</>}
                        {' · '}<span className={`food-picker-vis ${sheet.visibility}`}>{t(`pairing.picker.vis.${sheet.visibility}`)}</span>
                        {phaseMeans(sheet) && <small className="food-picker-means">{phaseMeans(sheet)}</small>}
                      </span>
                      <span className="food-picker-sheet-key">{keyValues(sheet)}</span>
                    </label>
                  ))}
                  <div className="food-picker-actions">
                    <button type="button" className="submit-btn" disabled={!chosenSheet} onClick={() => doImport('replace')}>
                      {t('pairing.foodpicker.import')}
                    </button>
                    <button type="button" className="reset-btn" disabled={!chosenSheet || !sameDish(dish)} onClick={() => doImport('complete')}
                      title={sameDish(dish) ? t('pairing.foodpicker.complete_hint') : t('pairing.foodpicker.other_dish')}>
                      {t('pairing.foodpicker.complete')}
                    </button>
                    <small className="import-hint">{sameDish(dish) ? t('pairing.foodpicker.import_hint') : `${t('pairing.foodpicker.import_hint')} ${t('pairing.foodpicker.other_dish')}`}</small>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default FoodSheetPicker;
